import { type BoxProps, Flex, Radio, forwardRef, type RadioProps } from '@/ui';

type Props = Omit<BoxProps, 'onClick'> & {
  radioProps?: RadioProps;
  isChecked: boolean;
  /** Accessible name for the row's radio — the row's keyboard select control. */
  label: string;
  onClick: () => void;
};

export const Row = forwardRef<Props, 'div'>(
  (
    {
      isChecked,
      label,
      onClick,
      style,
      radioProps = {},
      onPointerDown,
      ...rest
    },
    ref
  ) => {
    const { colorScheme: radioColorScheme, ...restRadioProps } = radioProps;

    return (
      <Flex
        align="center"
        h="34px"
        ref={ref}
        style={style}
        w="100%"
        onPointerDown={onPointerDown}
      >
        <Radio
          colorScheme={radioColorScheme ?? 'blackAlpha'}
          mr="2"
          {...restRadioProps}
          isChecked={isChecked}
          inputProps={{
            'aria-label': label,
            onKeyDown: (event) => {
              if (event.key === ' ') {
                // Suppress native radio activation so its synthesized click
                // (bubbling to the Radio's onClick) can't fire onClick twice.
                event.preventDefault();
                onClick();
              }
            },
          }}
          onClick={onClick}
        />

        {/* Mouse-only click surface: the labeled radio is the row's keyboard
            select control, which keeps content controls (the edit button, a
            real <button>) free of interactive ancestors. */}
        <Flex
          align="center"
          bg="white" // White background so colors come through properly even in dark modes
          borderRadius="8px"
          borderWidth="0"
          cursor="pointer"
          flex="1"
          fontSize="14"
          h="100%"
          overflow="hidden"
          transition="background-color 0.2s ease"
          onClick={onClick}
        >
          <Flex
            align="center"
            borderColor="transparent"
            borderWidth="1px"
            flex="1"
            h="100%"
            px="3"
            w="100%"
            {...rest}
          />
        </Flex>
      </Flex>
    );
  }
);
